import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { getGlobalCountry } from '@/atoms/countryState';

import {
  getAuthTokenFromCookie,
  setAuthToken,
} from '../components/shared/ManageTokenForClient/tokenManager';

export interface ApiClientOptions {
  baseURL: string;
  timeout: number;
  isSchool?: boolean;
  cookieName?: string;
  useBearer?: boolean;
  referer?: string;
  isAskAi?: boolean;
  injectCountry?: boolean;
}

let activeCountriesPromise: Promise<any> | null = null;

const ensureActiveCountriesInStorage = async (): Promise<void> => {
  if (typeof window === 'undefined') return;

  const cached = localStorage.getItem('/api/v4/active-countries');
  if (cached) return;

  if (!activeCountriesPromise) {
    activeCountriesPromise = (async () => {
      try {
        const { getActiveCountries } = await import('@/services/Cart');
        await getActiveCountries();
      } catch (err) {
        console.warn(
          'Failed to pre-fetch active countries in request interceptor:',
          err,
        );
      } finally {
        activeCountriesPromise = null;
      }
    })();
  }
  try {
    await Promise.race([
      activeCountriesPromise,
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
  } catch (error) {
    console.warn('Active countries pre-fetch timed out or failed:', error);
  }
};

const createApiClient = ({
  baseURL,
  timeout,
  isSchool = false,
  cookieName,
  useBearer = true,
  referer,
  isAskAi = false,
  injectCountry = false,
}: ApiClientOptions): AxiosInstance => {
  const apiClient = axios.create({
    baseURL,
    timeout,
  });

  apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (cookieName) {
        const authToken = getAuthTokenFromCookie(cookieName);
        config.headers = config.headers || {};
        if (authToken && !isAskAi) {
          config.headers['Authorization'] = useBearer
            ? `Bearer ${authToken}`
            : authToken;
        }
        if (referer) {
          config.headers['Referer'] = referer;
        }
      }

      if (injectCountry && !config.url?.includes('/api/v4/active-countries')) {
        await ensureActiveCountriesInStorage();

        const country = getGlobalCountry();

        config.headers = config.headers || {};

        if (typeof config.headers.set === 'function') {
          config.headers.set('country', country.countryCode);
        } else {
          // Must be countryCode (string), not the whole country object
          (config.headers as any)['country'] = country.countryCode;
        }
      }

      return await setAuthToken(config, isSchool, useBearer, isAskAi);
    },
  );

  return apiClient;
};

export default createApiClient;
